import type { RegularFaq } from '@/types/domains/RegularType';

/**
 * 定期コースのよくあるご質問。支給デザイン `regular.html` の `regular-course-faq-item` 10件。
 *
 * **支給を正として、旧 15件から差し替えた**（2026-08-13 事業側判断）。
 * 旧ページは const 15件 ＋ `index.vue` にベタ書きの1件（「その都度注文するより〜」）で 16件だった。
 * 支給10件に対応するのは9件で、**落ちるのは6問** — お届けサイクルの日にち指定・数量変更／
 * 受取拒否時の扱い／納品時の書面／エニストスペシャルケアセット割引の3問（PR に一覧を明記）。
 * 支給の回答には**新規作成の図版12点**（`img_regular-course002`〜`007` の PC/SP）が入っており、
 * 旧側の `.gif` 4点は使わなくなる。
 *
 * **3件目は支給の Q とリード文が2件目からコピペされていた**（本文だけ別内容で噛み合っていない。
 * DESIGN-FEEDBACK #38）。支給の**サポート導線98本が3番目に挙げている設問**
 * 「手元になくなってしまった場合、早めに送ってもらうことはできますか？」が正しいので、
 * そちらに直した（決定#51・2026-08-13 事業側判断）。直さないと 98本のリンク先が実在しなくなる。
 *
 * **`answerBody` は支給のHTMLをそのまま持つ。** `<br>` の改行位置と `<span>` の強調
 * （`w-strong` と素の `span`）が含まれており、プレーンテキストにすると落ちる。表示側は
 * `v-html` で出す（出どころは支給デザインだけで、ユーザー入力は混ざらない）。
 * **支給の本文に `<a>` は1本も無い**ので、リンクは持っていない。
 * 画像の参照先は `./assets/images/...` から `/image/...` に置き換えてある。
 *
 * **旧側にあった内部リンクは無くなる** — `/contact` へのインラインリンク2本と
 * `/guide/mypage#mypageSchedule` へのブロックリンク3本。支給の本文では「お問い合わせフォーム」
 * 「マイページ」が素のテキストになっている（`/guide/mypage` は PR #1284 で削除される）。
 *
 * **`question` は素の文字列。** サポート導線（`organisms/support/regularFaq.vue`）が
 * 先頭5件をそのまま出すので、装飾を持たせない。支給のサポート導線5件と先頭5件が一致する。
 */
/**
 * `/regular` の設問へのアンカーID。**並びから導出する**（決定#5）。
 *
 * サポート導線（`organisms/support/regularFaq.vue`）は先頭5件を出し、`/regular` 本体
 * （`organisms/regular/index.vue`）は10件すべてを出す。**どちらも `REGULAR_FAQ_LIST` を
 * 同じ順で読む**ので、添字が揃う。ID を設問ごとに持たせると出どころが2箇所になるため持たせない。
 *
 * 支給はサポート側のリンクを `href="#"` のプレースホルダにしており（決定#33）、飛び先は
 * 実装で決める。ハッシュ無しだと**どの設問を押しても `/regular` の先頭に着地する**。
 */
export const regularFaqAnchorId = (index: number): string => `regular-faq-${index + 1}`;

export const REGULAR_FAQ_LIST: ReadonlyArray<RegularFaq> = [
  {
    question: '定期コースについて教えてください。',
    answerLead: '定期コースとは、自動的に商品をお届けする大変お得なコースです。',
    answerBody: `<p class="regular-course-faq-a_text">
  初回のお届け日を基準に、ご指定の間隔で定期的に商品をお届けします。割引特典や送料無料など便利でお得にお買い物できるサービスです。<br /><br />
  毎日の元気やキレイのために長く続けていただきたいという思いから定期コースだけの特典をご用意しています。<br /><br />
  <span class="w-strong">■定期コースの5大特典</span
  ><br />
  <span class="w-strong"
    >【特典1：お得な割引価格でお届け】</span
  >定期コースだけの特別価格で購入できます。<br />
  <span class="w-strong"
    >【特典2：毎回、送料・代引手数料無料】</span
  >送料・代引き手数料はすべて当社が負担します。<br />
  <span class="w-strong"
    >【特典3：お届け日や数量も変更可能】</span
  >お届け日や個数の変更も自由に指定できます。<br />
  <span class="w-strong"
    >【特典4：電話や問い合わせフォームで変更OK】</span
  >変更手続きはマイページ・お電話で変更可能です。<br />
  <span class="w-strong">【特典5：お休みもOK】</span
  >一時休止もできるので安心です♪<br /><br />
  ※定期コースには、商品ごとにお受け取り回数のお約束がございます。詳細は各商品ページをご確認ください。<br />
  ※お約束の回数を受け取り後は、いつでも休止・ご解約いただけます。お届け日の変更・お休み・ご解約をご希望の場合は、
  <span>
    お届け予定日の10日前までにご連絡ください。
  </span>
</p>`,
  },
  {
    question: 'その都度注文するよりどれくらいお得なのでしょうか？',
    answerLead: '長く続けたい人ほど、とってもお得です！',
    answerBody: `<p class="regular-course-faq-a_text">
  毎日の元気やキレイのために長く続けていただきたいという思いから、定期コースだけのお得な割引価格をご用意しています。詳しくは各商品の詳細ページをご覧ください。
</p>`,
    images: [
      {
        pc: '/image/regular/img_regular-course002.png',
        sp: '/image/regular/img_regular-course002_sp.png',
        alt: 'ラフィーネ-アルファ 毎月3箱ずつお届けの場合の割引例',
      },
      {
        pc: '/image/regular/img_regular-course003.png',
        sp: '/image/regular/img_regular-course003_sp.png',
        alt: 'ラフィーネエパゴールド 600 毎月1袋ずつお届けの場合の割引例',
      },
      {
        pc: '/image/regular/img_regular-course004.png',
        sp: '/image/regular/img_regular-course004_sp.png',
        alt: 'アレルナイトプラス 毎月30本ずつお届けの場合の割引例',
      },
    ],
  },
  {
    question: '手元になくなってしまった場合、早めに送ってもらうことはできますか？',
    answerLead: 'お届け日や個数の変更は可能です。',
    answerBody: `<p class="regular-course-faq-a_text">
  「足りなくなった」「家族も一緒に飲み始めた」など、お客様のご都合に合わせてお届け日や製品の個数を変更することができます。<br />
  また追加のご注文も定期コースの割引価格でご購入できるのでお得です。<br />
  <span>お届け予定日の10日前にご連絡ください。</span>
</p>`,
    images: [
      {
        pc: '/image/regular/img_regular-course005.png',
        sp: '/image/regular/img_regular-course005_sp.png',
        alt: '（例）次回のお届け予定日が5月20日の場合、10日前までのご連絡でお届け日を前倒しできる',
      },
    ],
  },
  {
    question: 'つい飲み忘れてたくさん余ってしまったのですが、お休みできますか？',
    answerLead: 'お届けをお休みすることができます。',
    answerBody: `<p class="regular-course-faq-a_text">
  お手元に製品が余っているときや、長期不在で製品を受け取れないときにご利用ください。<br />
  基本的に2ヶ月程度お休みすることができます。マイページの定期お届け情報詳細、もしくはお電話・お問い合わせフォームにてご連絡ください。<br />
  お休みのご連絡は<span>お届け予定日の10日前</span>までにお願い致します。
</p>`,
  },
  {
    question: '受け取り回数の条件は商品によって違いますか？',
    answerLead:
      'はい、違います。ただし、同じ商品で数量のみご変更があった場合は、合計の受取回数でカウント致します。',
    answerBody: `<p class="regular-course-faq-a_text">
  ・【定期】ラフィーネ-アルファ1箱
  →【定期】ラフィーネ-アルファ2箱 へ変更<br />
  ・【定期】ラフィーネエパゴールド 300
  →【定期】ラフィーネエパゴールド 600 へ変更<br />
  上記の場合、ご変更前後の合計で3回以上お受取りいただきます。
</p>`,
    images: [
      {
        pc: '/image/regular/img_regular-course006.png',
        sp: '/image/regular/img_regular-course006_sp.png',
        alt: '定期コースの数量変更と受取回数の数え方の例',
      },
    ],
  },
  {
    question: '受け取り回数以上受け取った後はマイページで解約できますか？',
    answerLead: 'お電話 または お問い合わせフォームにてご連絡ください。',
    answerBody: `<p class="regular-course-faq-a_text">
  ご解約をご希望の際は<span>お届け予定日の10日前</span>までに、お電話
  または お問い合わせフォーム にてご連絡ください。
</p>`,
  },
  {
    question: '定期コースのお届けサイクルは商品によって違うのですか？',
    answerLead: 'はい、商品ごとに異なります。',
    answerBody: `<p class="regular-course-faq-a_text">
  お届けのサイクルについては、各商品ページをご確認ください。お届け日のご指定は、日ごと・月ごとからお選びいただけます。<br />
  お届け日の変更はマイページよりお手続きいただけるほか、お問い合わせフォームまたはお電話でも承っております。
</p>`,
    images: [
      {
        pc: '/image/regular/img_regular-course007.png',
        sp: '/image/regular/img_regular-course007_sp.png',
        alt: 'あなたのご希望のサイクルで、お届け日をご指定できます（容量の多い商品のみ最長2ヶ月のサイクルでお届け可能）',
      },
    ],
  },
  {
    question:
      '定期中に、足りなくなってしまって、毎回の定期とは別に1箱追加で購入したい場合はどうしたら良いですか？',
    answerLead: 'お届け日の前倒しをお申しつけください。',
    answerBody: `<p class="regular-course-faq-a_text">
  お手元に足りなくなってしまった場合は、お届け日の前倒しをお申しつけください。お届けの価格は定期購入時と同じ価格となります。
</p>`,
  },
  {
    question: '納品書などに次回定期日は記載されていますか？',
    answerLead: 'はい、記載しております。',
    answerBody: `<p class="regular-course-faq-a_text">
  お届け時のお買い上げ明細書に記載しております。
</p>`,
  },
  {
    question: '配達時間帯の変更は可能ですか？',
    answerLead: '配達時間帯の変更が可能です。',
    answerBody: `<p class="regular-course-faq-a_text">
  宅配便でお届けする商品は、お時間のご指定が可能です。ご希望の場合は、お届け予定日の10日前までに、マイページからお手続きいただくか、お問い合わせフォームまたはお電話にてご連絡ください。<br />
  なお、「ポストにお届け」の商品につきましては、お時間のご指定を承っておりません。
</p>`,
  },
];
